import { lightningReportErrorEllipseConfidences } from '@xweather/protect-types'
import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box } from '../../../../../components/Box.tsx'
import { Alert, FormControlLabel, FormLabel, Switch, ToggleButton, ToggleButtonGroup, Tooltip } from '../../../../../components/generic.tsx'
import { I18n } from '../../../../../components/I18n.tsx'
import { Stack } from '../../../../../components/Stack.tsx'
import { useLightningReportTypeRules } from '../../../../../data/analyze/reportTypeRules.ts'
import { ids } from '../../../../../ids.ts'
import { SignalStrengthSlider } from '../../../SignalStrengthSlider.tsx'
import { type LightningReportForm, useLightningFormValues } from '../schema.tsx'

export const ErrorEllipseConfidenceField = () => {
	const { setValue } = useFormContext<LightningReportForm>()

	const { errorEllipses: doesSubscriptionSupportErrorEllipses } = useLightningReportTypeRules()
	const [errorEllipseConfidence] = useLightningFormValues(['errorEllipseConfidence'])
	return (
		<Tooltip title={!doesSubscriptionSupportErrorEllipses && <I18n i18nKey={($) => $.reports.featureNotAvailableForSubscription} />}>
			<Stack direction='column'>
				<FormControlLabel
					control={
						<Switch
							data-ta={ids.reports.form.lightning.reportDetails.errorEllipsesToggle}
							disabled={!doesSubscriptionSupportErrorEllipses}
							checked={errorEllipseConfidence != null}
							onChange={(_, checked) => {
								const defaultErrorEllipseConfidence = 50
								setValue('errorEllipseConfidence', checked ? defaultErrorEllipseConfidence : undefined)
							}}
						/>
					}
					label={<I18n i18nKey={($) => $.reports.lightning.form.errorEllipsesEnabled} />}
				/>
				{errorEllipseConfidence != null && (
					<Box>
						<FormLabel>{<I18n i18nKey={($) => $.reports.lightning.form.errorEllipsesConfidence} />}</FormLabel>
						<ToggleButtonGroup fullWidth>
							{lightningReportErrorEllipseConfidences.map((confidence) => (
								<ToggleButton
									data-ta={ids.reports.form.lightning.reportDetails.errorEllipseConfidenceSelection(confidence)}
									key={confidence}
									disabled={!doesSubscriptionSupportErrorEllipses || errorEllipseConfidence == null}
									value={confidence}
									selected={errorEllipseConfidence === confidence}
									onClick={() => setValue('errorEllipseConfidence', confidence)}
								>
									{confidence}%
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					</Box>
				)}
			</Stack>
		</Tooltip>
	)
}

export const IncludeStrikeDamagePotentialField = () => {
	const { control } = useFormContext<LightningReportForm>()
	const { strikeDamagePotential: doesSubscriptionSupportStrikeDamagePotential } = useLightningReportTypeRules()

	return (
		<Tooltip
			title={!doesSubscriptionSupportStrikeDamagePotential && <I18n i18nKey={($) => $.reports.featureNotAvailableForSubscription} />}
		>
			<span>
				<Controller
					name='includeStrikeDamagePotential'
					control={control}
					render={({ field }) => (
						<FormControlLabel
							control={
								<Switch
									{...field}
									data-ta={ids.reports.form.lightning.reportDetails.strikeDamagePotentialToggle}
									disabled={!doesSubscriptionSupportStrikeDamagePotential}
									checked={field.value}
								/>
							}
							label={<I18n i18nKey={($) => $.reports.lightning.form.strikeDamagePotentialEnabled} />}
						/>
					)}
				/>
			</span>
		</Tooltip>
	)
}

export const IncludeCloudToCloudField = () => {
	const { control } = useFormContext<LightningReportForm>()
	return (
		<Controller
			name='includeCloudToCloud'
			control={control}
			render={({ field }) => (
				<FormControlLabel
					control={<Switch data-ta={ids.reports.form.lightning.reportDetails.cloudToCloudEventsToggle} {...field} checked={field.value} />}
					label={<I18n i18nKey={($) => $.reports.lightning.form.cloudToCloudEventsEnabled} />}
				/>
			)}
		/>
	)
}

export const SignalStrengthFilterField = () => {
	const { t } = useTranslation()
	const { setValue } = useFormContext<LightningReportForm>()
	const [persistedRange] = useLightningFormValues(['signalStrengthRange'])
	const [pendingRange, setPendingRange] = useState<LightningReportForm['signalStrengthRange'] | undefined>(undefined)

	const signalStrengthRange = pendingRange ?? persistedRange
	const [filterType, setFilterType] = useState<'all' | 'positive' | 'custom'>(() => {
		if (signalStrengthRange.min === undefined && signalStrengthRange.max === undefined) {
			return 'all'
		} else if (signalStrengthRange.min === 0 && signalStrengthRange.max === undefined) {
			return 'positive'
		}

		return 'custom'
	})

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: It is a simple function with many conditions
	const { disclaimer, error: disclaimerError } = (() => {
		const { min, max, inverted } = signalStrengthRange
		const isMinInfinite = min === undefined
		const isMaxInfinite = max === undefined
		if (isMinInfinite && isMaxInfinite) {
			if (inverted) {
				return { disclaimer: <I18n i18nKey={($) => $.reports.lightning.form.signalStrengthFilter.descriptions.excludesAll} />, error: true }
			} else {
				return { disclaimer: <I18n i18nKey={($) => $.reports.lightning.form.signalStrengthFilter.descriptions.includesAll} /> }
			}
		}

		if (inverted) {
			if (!isMinInfinite && !isMaxInfinite) {
				return { disclaimer: <I18n i18nKey={($) => $.reports.lightning.form.signalStrengthFilter.descriptions.or} values={{ min, max }} /> }
			} else if (isMinInfinite) {
				return {
					disclaimer: (
						<I18n i18nKey={($) => $.reports.lightning.form.signalStrengthFilter.descriptions.above} values={{ signalStrength: max }} />
					),
				}
			} else {
				return {
					disclaimer: (
						<I18n i18nKey={($) => $.reports.lightning.form.signalStrengthFilter.descriptions.below} values={{ signalStrength: min }} />
					),
				}
			}
		} else {
			if (!isMinInfinite && !isMaxInfinite) {
				return {
					disclaimer: <I18n i18nKey={($) => $.reports.lightning.form.signalStrengthFilter.descriptions.between} values={{ min, max }} />,
				}
			} else if (isMinInfinite) {
				return {
					disclaimer: (
						<I18n i18nKey={($) => $.reports.lightning.form.signalStrengthFilter.descriptions.below} values={{ signalStrength: max }} />
					),
				}
			} else {
				return {
					disclaimer: (
						<I18n i18nKey={($) => $.reports.lightning.form.signalStrengthFilter.descriptions.above} values={{ signalStrength: min }} />
					),
				}
			}
		}
	})()

	const onChangePending = (range: LightningReportForm['signalStrengthRange']) => setPendingRange(range)
	const onChangeCommited = (range: LightningReportForm['signalStrengthRange']) => {
		setValue('signalStrengthRange', range, { shouldValidate: true })
		setPendingRange(undefined)
	}
	return (
		<Box>
			<FormLabel>{t(($) => $.reports.lightning.form.signalStrengthFilter.label)} </FormLabel>
			<ToggleButtonGroup value={filterType} fullWidth>
				<ToggleButton
					data-ta={ids.reports.form.lightning.reportDetails.signalStrengthFilter('all')}
					value='all'
					selected={filterType === 'all'}
					onClick={() => {
						setFilterType('all')
						onChangeCommited({ min: undefined, max: undefined, inverted: false })
					}}
				>
					{t(($) => $.reports.lightning.form.signalStrengthFilter.all)}
				</ToggleButton>
				<ToggleButton
					data-ta={ids.reports.form.lightning.reportDetails.signalStrengthFilter('positive')}
					value='positive'
					selected={filterType === 'positive'}
					onClick={() => {
						setFilterType('positive')
						onChangeCommited({ min: 0, max: undefined, inverted: false })
					}}
				>
					{t(($) => $.reports.lightning.form.signalStrengthFilter.positiveOnly)}
				</ToggleButton>
				<ToggleButton
					data-ta={ids.reports.form.lightning.reportDetails.signalStrengthFilter('custom')}
					value='custom'
					selected={filterType === 'custom'}
					onClick={() => {
						setFilterType('custom')
						onChangeCommited({ min: undefined, max: undefined, inverted: false })
					}}
				>
					{t(($) => $.reports.lightning.form.signalStrengthFilter.custom)}
				</ToggleButton>
			</ToggleButtonGroup>
			{filterType === 'custom' && (
				<Stack direction='column'>
					<SignalStrengthSlider
						dataTa={ids.reports.form.lightning.reportDetails.customSignalStrengthSlider.root}
						min={-100}
						max={200}
						infiniteOption
						range={signalStrengthRange}
						onChange={onChangePending}
						onChangeCommited={onChangeCommited}
						additionalMarks={[{ label: '0 kA', value: 0 }]}
					/>
					<Alert severity={disclaimerError ? 'error' : 'info'}>{disclaimer}</Alert>
				</Stack>
			)}
		</Box>
	)
}
